import useJobStream from '../hooks/useJobStream';
import LiveJobStatus from '../components/LiveJobStatus';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import {
  getClientRuntimeAvailability,
  isAiRiggingJobCreationEnabled,
  operationLabel,
} from '../features/aiRigging';
import { shortHash, shortJobId } from '../features/riggingJobStatus';
import RiggingJobStatusBadge from '../components/rigging/RiggingJobStatusBadge';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';
import ProjectBreadcrumbs from '../components/ProjectBreadcrumbs';
import { hasTenantPermission, RIGGING_JOB_CREATE, RIGGING_JOB_VIEW } from '../auth/permissions';
import { useTenantPermissions } from '../hooks/useTenantPermissions';

function EmptyState({ title, copy, actionLabel, actionTo }) {
  return (
    <div className="product-card empty-state" data-testid="rigging-jobs-empty">
      <h2 className="h5">{title}</h2>
      <p className="text-muted mb-3">{copy}</p>
      {actionTo ? (
        <Button as={Link} to={actionTo} variant="outline-primary">
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function StatusSummary({ statusCounts = {}, totalCount = 0 }) {
  const cells = [
    ['Total', totalCount],
    ['Queued', statusCounts.queued || 0],
    ['Running', statusCounts.running || 0],
    ['Succeeded', statusCounts.succeeded || 0],
    ['Failed', statusCounts.failed || 0],
    ['Cancelled', statusCounts.canceled || 0],
  ];
  return (
    <div className="row g-2 mb-3" data-testid="rigging-jobs-status-summary">
      {cells.map(([label, value]) => (
        <div className="col-6 col-md-2" key={label}>
          <div className="product-card py-2 px-3 h-100">
            <div className="text-muted small">{label}</div>
            <div className="fs-5 fw-semibold mb-0">{value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProjectRiggingJobsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, hasTenantPermission: can } = useTenantPermissions();
  const [project, setProject] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [memory, setMemory] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusFilter = searchParams.get('status') || '';
  const operationFilter = searchParams.get('operation') || '';
  const characterFilter = searchParams.get('character_id') || '';
  const search = searchParams.get('search') || '';

  const canView =
    can(RIGGING_JOB_VIEW) || hasTenantPermission(permissions, RIGGING_JOB_VIEW);
  const canCreate =
    (can(RIGGING_JOB_CREATE) || hasTenantPermission(permissions, RIGGING_JOB_CREATE)) &&
    isAiRiggingJobCreationEnabled();
  const { revision: liveRevision, status: liveStatus } = useJobStream({ projectId, rigging: true, enabled: canView });

  const runtime = availability || getClientRuntimeAvailability();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (liveRevision === 0) setLoading(true);
      setError(null);
      try {
        const [projectData, jobPayload, chars, mem, avail] = await Promise.all([
          bakingApi.getProject(projectId).catch(() => null),
          bakingApi.listRiggingJobs(projectId, {
            status: statusFilter,
            operation: operationFilter,
            character_id: characterFilter,
            search,
          }),
          bakingApi.listRigCharacters(projectId).catch(() => []),
          bakingApi.getProjectRigMemory(projectId).catch((err) => {
            if (err.status === 404) return null;
            throw err;
          }),
          bakingApi.getRiggingRuntimeAvailability(projectId).catch(() => null),
        ]);
        if (cancelled) return;
        setProject(projectData);
        setJobs(jobPayload?.results || []);
        setStatusCounts(jobPayload?.status_counts || {});
        setTotalCount(
          typeof jobPayload?.total_count === 'number'
            ? jobPayload.total_count
            : (jobPayload?.results || []).length
        );
        setCharacters(chars || []);
        setMemory(mem);
        setAvailability(avail || jobPayload?.runtime_availability || null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load rigging jobs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, statusFilter, operationFilter, characterFilter, search, liveRevision]);

  const publishedCharacters = useMemo(
    () => (characters || []).filter((c) => c.status !== 'archived' && c.active_memory_version),
    [characters]
  );

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  if (!canView && permissions.length) {
    return (
      <div className="error-state" data-testid="rigging-jobs-forbidden">
        You do not have permission to view rigging jobs.
      </div>
    );
  }

  let empty = null;
  if (!loading && !error && jobs.length === 0) {
    if (!memory) {
      empty = (
        <EmptyState
          title="Set up Project Rig Memory first"
          copy="Rigging jobs require a published project policy and a published character Rig Memory version."
          actionLabel="Set Up Project Memory"
          actionTo={`/console/projects/${projectId}/rigging/project-memory`}
        />
      );
    } else if (characters.length === 0) {
      empty = (
        <EmptyState
          title="Create a character first"
          copy="Add a character and publish its Rig Memory before creating a rigging job."
          actionLabel="Create Character"
          actionTo={`/console/projects/${projectId}/rigging/characters`}
        />
      );
    } else if (publishedCharacters.length === 0) {
      empty = (
        <EmptyState
          title="Publish a character Rig Memory"
          copy="Jobs can only use immutable published Rig Memory versions."
          actionLabel="View Characters"
          actionTo={`/console/projects/${projectId}/rigging/characters`}
        />
      );
    } else {
      empty = (
        <EmptyState
          title="No rigging jobs yet"
          copy="Create a job from a published character Rig Memory and an immutable project source revision."
          actionLabel={canCreate ? 'New Rigging Job' : null}
          actionTo={canCreate ? `/console/projects/${projectId}/rigging/jobs/new` : null}
        />
      );
    }
  }

  const projectName = project?.name || 'Project';

  return (
    <div data-testid="rigging-jobs-page">
      <LiveJobStatus status={liveStatus} />
      <ProjectBreadcrumbs
        items={[
          { label: 'Projects', to: '/console/projects' },
          { label: projectName, to: `/console/projects/${projectId}` },
          { label: 'Rigging Jobs' },
        ]}
      />
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="product-page-title">Rigging Jobs</h1>
          <p className="product-page-subtitle mb-0">
            Create and monitor Maya character-rigging operations using published Project Rig Memory
            and immutable source revisions.
          </p>
        </div>
        <Button
          disabled={!canCreate}
          onClick={() => navigate(`/console/projects/${projectId}/rigging/jobs/new`)}
          data-testid="new-rigging-job-action"
          title={
            !canCreate
              ? 'Job creation requires permission and AI_RIGGING_JOB_CREATION_ENABLED'
              : undefined
          }
        >
          New Rigging Job
        </Button>
      </div>

      <p className="mb-3 small">
        <Link to={`/console/projects/${projectId}/rigging`}>Rigging configuration</Link>
        {' · '}
        Project Rig Memory, characters, decisions, and proposals
      </p>

      <RiggingRuntimeNotice availability={runtime} className="mb-3" />

      {!canCreate && isAiRiggingJobCreationEnabled() === false ? (
        <Alert variant="info" className="mb-3">
          New Rigging Job is unavailable because job creation is disabled. You can still review
          existing jobs and prepare Project Rig Memory.
        </Alert>
      ) : null}

      {!loading && !error ? (
        <StatusSummary statusCounts={statusCounts} totalCount={totalCount} />
      ) : null}

      <Form className="row g-2 mb-3">
        <div className="col-md-3">
          <Form.Label visuallyHidden>Search</Form.Label>
          <Form.Control
            placeholder="Search jobs"
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            aria-label="Search jobs"
          />
        </div>
        <div className="col-md-2">
          <Form.Select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="canceled">Cancelled</option>
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Form.Select
            aria-label="Character filter"
            value={characterFilter}
            onChange={(e) => updateFilter('character_id', e.target.value)}
          >
            <option value="">All characters</option>
            {characters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-4">
          <Form.Select
            aria-label="Operation filter"
            value={operationFilter}
            onChange={(e) => updateFilter('operation', e.target.value)}
          >
            <option value="">All operations</option>
            <option value="maya_project_setup">Project Setup</option>
            <option value="maya_skeleton_build">Build Skeleton</option>
            <option value="maya_rig_build">Build Rig</option>
            <option value="maya_ai_autoskin">AI AutoSkin</option>
            <option value="maya_engine_export">Engine Export</option>
            <option value="maya_character_pipeline">Complete Character Pipeline</option>
          </Form.Select>
        </div>
      </Form>

      {loading ? (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading rigging jobs…
        </div>
      ) : null}
      {error ? <div className="error-state">{error}</div> : null}
      {empty}
      {!loading && !error && jobs.length > 0 ? (
        <div className="product-card p-0">
          <Table responsive hover className="product-table mb-0">
            <thead>
              <tr>
                <th>Job</th>
                <th>Character</th>
                <th>Operation</th>
                <th>Rig Memory</th>
                <th>Source Revision</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Created</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <Link to={`/console/projects/${projectId}/rigging/jobs/${job.id}`}>
                      {shortJobId(job.id)}
                    </Link>
                  </td>
                  <td>{job.character_display_name || '—'}</td>
                  <td>{operationLabel(job.operation)}</td>
                  <td className="mono small">
                    v{job.memory_version_number || '—'}
                    {job.execution_package_sha256
                      ? ` · ${shortHash(job.execution_package_sha256)}`
                      : ''}
                  </td>
                  <td className="small">
                    {job.source_type === 'perforce' && job.source_resolved_changelist
                      ? `Changelist ${job.source_resolved_changelist}`
                      : job.source_type === 'archive'
                        ? `Uploaded · ${shortHash(job.source_content_sha256 || job.source_revision_id)}`
                        : shortJobId(job.source_revision_id)}
                  </td>
                  <td>
                    <RiggingJobStatusBadge
                      job={job}
                      workerRuntimeEnabled={Boolean(
                        runtime.worker_runtime_enabled ?? runtime.workerRuntimeEnabled
                      )}
                    />
                  </td>
                  <td>{job.attempt_count ?? '—'}</td>
                  <td className="small">
                    {job.created_at ? new Date(job.created_at).toLocaleString() : '—'}
                  </td>
                  <td>{job.created_by_username || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
